<template>
    <div class="table-outer-wrapper" :class="{ 'no-top-filters': props.hideTopFilters }">
        <div
            v-if="!props.hideTopFilters"
            class="header-row"
            :class="{
                'reduced-header': (props.optionsTeleport || props.hideButtonRow) && !hasSlotHeader && !props.header,
            }"
        >
            <h4 v-if="!hasSlotHeader && props.header">{{ props.header }}</h4>
            <slot name="header" />
            <slot name="header-extra" />
            <div
                v-if="!props.hideButtonRow && showTableActionButtons"
                class="button-row"
            >
                <conditional-teleport :to="props.optionsTeleport || null">
                    <div class="icon-flex">
                        <action-button
                            v-if="showEditTableButton"
                            class="icon-controls"
                            title="Edit table"
                            :icon="['fas', 'pencil']"
                            :show-title-on-mobile-view="!!props.optionsTeleport"
                            :is-disabled="props.isLoading"
                            @click="showEditTableOverlay"
                        />
                        <action-button
                            v-if="showDownloadTableButton"
                            class="icon-controls"
                            title="Download table"
                            :icon="['fas', 'download']"
                            :show-title-on-mobile-view="!!props.optionsTeleport"
                            :is-disabled="props.isLoading"
                            @click="openDownloadTableOverlay"
                        />
                    </div>
                </conditional-teleport>
            </div>
        </div>
        <div
            v-if="initialTableData.length"
            class="table-wrapper"
            :class="{ inverted: props.inverted }"
            ref="tableWrapperRef"
            @scroll="scrollTable"
        >
            <table
                ref="tableElementRef"
                :class="{ 'fixed-columns': fixedGridColumns }"
                :style="{ gridTemplateColumns: fixedGridColumns ?? tableGrid.templateColumns }"
            >
                <thead v-if="!props.inverted">
                    <tr v-if="parentHeaders.length" class="parent-header">
                        <th
                            v-for="{ header: parentHeader, gridColumn, index } in parentHeaderCells"
                            class="bordered"
                            :key="`parent-header-${index}`"
                            :style="{ gridColumn, gridRow: '1' }"
                            :colspan="parentHeader.columnSpan"
                            v-tooltip="parentHeader.hoverText ? escapeHtml(parentHeader.hoverText) : null"
                        >
                            <span>{{ getParentHeaderTitle(parentHeader) }}</span>
                        </th>
                    </tr>
                    <tr>
                        <th v-if="props.checkable" class="bordered check-header" :style="{ gridColumn: '1', gridRow: parentHeaders.length ? '1 / span 2' : '1' }" :rowspan="parentHeaders.length ? 2 : 1">
                            <i class="fa-solid fa-check" />
                        </th>
                        <th
                            v-for="(header, headerIndex) in displayedTableHeaders"
                            v-tooltip="header.hoverText ? escapeHtml(header.hoverText) : null"
                            :class="{
                                clickable: header.allowSort || header.allowFilter,
                                'small-rows': props.smallHeader,
                                selected: header.selected,
                                'stick-left': headerSticksLeft(header),
                                bordered: headerHasBorder(header, headerIndex),
                                'no-right-border': headerShouldHideRightBorder(headerIndex),
                            }"
                            :style="getHeaderStyle(header)"
                            :key="header.id"
                            :rowspan="header.rowSpan"
                        >
                            <div v-if="!header.allowSort && !header.allowFilter" class="column-content">
                                {{ getHeaderTitle(header, headerIndex) }}
                            </div>
                            <prime-button v-else class="header-button" text @click="openHeaderFilterOverlay($event, header)">
                                <span>{{ getHeaderTitle(header, headerIndex) }}</span>
                                <span v-if="headerToSort.id === header.id || headerHasFilter(header.id)" class="header-controls" aria-hidden="true">
                                    <i v-if="headerToSort.id === header.id" :class="`fa-solid ${getSortingIcon(header.id)}`" />
                                    <i v-if="headerHasFilter(header.id)" class="fa-solid fa-filter" />
                                </span>
                            </prime-button>
                        </th>
                    </tr>
                </thead>
                <tbody
                    @mouseenter="showTooltip"
                    @focusin="showTooltip"
                    @mouseleave="hideTooltip"
                    @focusout="hideTooltip"
                    @mousemove="updateTooltipDebounced"
                >
                    <tr
                        v-for="(row, rowIndex) in visibleTableRows"
                        :key="`${row.uniqueId ?? 'row'}-${rowIndex}`"
                        :class="{
                            'small-rows': props.smallCells,
                            'pointer-cursor': props.rowClickMode !== 'disabled',
                            'hovered-row': props.hoveredRowId && row.uniqueId === props.hoveredRowId,
                            selected: row.selected,
                            boldRow: row.boldRow,
                        }"
                        @click="selectRow(row)"
                        v-tooltip.bottom="{ value: row.hoverOver, escape: false }"
                    >
                        <td v-if="props.checkable" :key="`${row.uniqueId ?? rowIndex}-checkbox`" class="checkbox-cell">
                            <checkbox :model-value="row.selected" readonly binary />
                        </td>
                        <td
                            v-for="([col, header, headerIndex, colspan]) in orderedRow(row)"
                            :key="col.cellKey ?? `${row.uniqueId ?? rowIndex}-${header.id}`"
                            :class="{
                                wrap: getHeaderOverride(col, 'allowWrap', header),
                                flash: col.flash,
                                'stick-left': col.freeze,
                                spannedCell: colspan > 1,
                            }"
                            :style="getCellStyle(col, headerIndex, colspan)"
                            :colspan="colspan > 1 ? colspan : undefined"
                            :data-tooltip="col.tooltip"
                            :data-cell-key="col.cellKey"
                        >
                            <div v-if="col.percent" class="percent-wrapper">
                                <div class="percent-bar" :style="getPercentStyle(col, headerIndex)" />
                            </div>
                            <div class="column-content">
                                <div v-if="col.valueToIcons" class="icon-row">
                                    <span
                                        v-for="({ icon, colour, tooltip }, key) in getIcons(col)"
                                        :key="key"
                                        :data-tooltip="tooltip ?? null"
                                    >
                                        <icofont-icon
                                            :icon="icon"
                                            :icon-colour="colour"
                                            size="small"
                                        />
                                    </span>
                                </div>
                                <slot
                                    v-else
                                    name="cell"
                                    :cell="col"
                                    :row="row"
                                    :header="header"
                                    :formatted="formatCellText(col, true, false, header)"
                                    :format-value="(v: string | number | null) => formatIndividualCellValue(v, col, true, false, header)"
                                >
                                    <span v-html="formatCellText(col, true, false, header)" />
                                </slot>
                                <span v-if="shouldRenderDirectionIcon(col)" class="direction-icon">
                                    {{ getDirectionIcon(col) }}
                                </span>
                            </div>
                            <div class="units-hover" v-if="chosenTableUnitsSettings === 'hover'">
                                {{ allHeaderSuffixesPrefixes[headerIndex] }}
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="props.inverted">
                    <tr v-if="parentHeaders.length" class="parent-header">
                        <th
                            v-for="{ header: parentHeader, gridColumn, index } in parentHeaderCells"
                            class="bordered"
                            :key="`parent-header-${index}`"
                            :style="{ gridColumn, gridRow: '1' }"
                            :colspan="parentHeader.columnSpan"
                            v-tooltip="parentHeader.hoverText ? escapeHtml(parentHeader.hoverText) : null"
                        >
                            <span>{{ getParentHeaderTitle(parentHeader) }}</span>
                        </th>
                    </tr>
                    <tr>
                        <th v-if="props.checkable" class="bordered check-header" :style="{ gridColumn: '1', gridRow: parentHeaders.length ? '1 / span 2' : '1' }" :rowspan="parentHeaders.length ? 2 : 1">
                            <i class="fa-solid fa-check" />
                        </th>
                        <th
                            v-for="(header, headerIndex) in displayedTableHeaders"
                            v-tooltip="header.hoverText ? escapeHtml(header.hoverText) : null"
                            :class="{
                                clickable: header.allowSort || header.allowFilter,
                                'small-rows': props.smallHeader,
                                selected: header.selected,
                                'stick-left': headerSticksLeft(header),
                                bordered: headerHasBorder(header, headerIndex),
                                'no-right-border': headerShouldHideRightBorder(headerIndex),
                            }"
                            :style="getHeaderStyle(header)"
                            :key="`inverted-${header.id}`"
                            :rowspan="header.rowSpan"
                        >
                            <div v-if="!header.allowSort && !header.allowFilter" class="column-content">
                                {{ getHeaderTitle(header, headerIndex) }}
                            </div>
                            <prime-button v-else class="header-button" text @click="openHeaderFilterOverlay($event, header)">
                                <span>{{ getHeaderTitle(header, headerIndex) }}</span>
                                <span v-if="headerToSort.id === header.id || headerHasFilter(header.id)" class="header-controls" aria-hidden="true">
                                    <i v-if="headerToSort.id === header.id" :class="`fa-solid ${getSortingIcon(header.id)}`" />
                                    <i v-if="headerHasFilter(header.id)" class="fa-solid fa-filter" />
                                </span>
                            </prime-button>
                        </th>
                    </tr>
                </tfoot>
            </table>
        </div>
        <div v-else>
            <p>{{ props.noDataFoundMessage ?? Messages.NoData }}</p>
        </div>
    </div>

    <plant-switch
        v-if="selectedPlantId"
        v-model:visible="showPlantModal"
        :plant-id="selectedPlantId"
        :from-date="props.plantModalDateRange?.from"
        :to-date="props.plantModalDateRange?.to"
        :initial-details-tab="props.initialPlantDetailsTab"
        @hide="selectedPlantId = null"
    />
    <popover
        class="arrow-colour-0"
        ref="headerSettingsOverlay"
        @hide="resetCurrentHeaderOverlay"
    >
        <generic-header-filter
            v-if="selectedHeaderToFilter && selectedPropertyForFilter"
            :key="selectedHeaderToFilter.id"
            :current-filters="currentFiltersByHeading[selectedHeaderToFilter.id] ?? null"
            :header-to-sort="headerToSort"
            :filter-property="selectedPropertyForFilter"
            :filter-header="selectedHeaderToFilter"
            @update:filters="updateFilters($event, selectedHeaderToFilter.id)"
            @update:sort-direction-ascending="updateSort($event, selectedHeaderToFilter.id)"
        >
            <template v-if="shouldUseCustomHeaderSortSlot(selectedHeaderToFilter.id)" #sort="slotProps">
                <slot name="header-sort-control" v-bind="slotProps" />
            </template>
        </generic-header-filter>
    </popover>
    <popover class="arrow-colour-0" ref="editColumnsOverlay">
        <edit-table-columns
            :table-headers="tableHeaders"
            :columns-to-hide="columnsToHide"
            :table-units-setting="tableUnitsSetting"
            :disable-reorder="props.disableReorder"
            @toggle:column="toggleShowTableColumn"
            @reorder:columns="updateColumnOrder"
            @update:table-units-setting="updateUnitLocation"
            @reset:headers="resetHeaders"
            @reset:table="resetTable"
        />
    </popover>
    <popover class="arrow-colour-0" ref="downloadOverlay">
        <table-download-type-select
            :file-name="downloadFileName"
            :format-function="formatDataToJson"
            @exit="closeDownloadTableOverlay"
        />
    </popover>
    <popover v-if="tooltipActive" class="arrow-colour-0" ref="tooltipOverlay">
        <div class="tooltip">
            {{ tooltipText }}
        </div>
    </popover>
    <conditional-teleport v-if="props.editTableTeleport" :to="props.editTableTeleport || null">
        <edit-table-columns
            :table-headers="tableHeaders"
            :columns-to-hide="columnsToHide"
            :table-units-setting="tableUnitsSetting"
            :disable-reorder="props.disableReorder"
            :is-teleport="true"
            @toggle:column="toggleShowTableColumn"
            @reorder:columns="updateColumnOrder"
            @update:table-units-setting="updateUnitLocation"
            @reset:headers="resetHeaders"
            @reset:table="resetTable"
        />
    </conditional-teleport>
    <download-excel
        ref="excelDownloadComponentRef"
        type="xlsx"
        :name="`${downloadFileName}.xlsx`"
        :fetch="formatDataToJson"
    >
        <div />
    </download-excel>
    <generic-table-filter-tags
        v-if="filterTags.length && props.filterTagsTeleport"
        :tags="filterTags"
        :to="props.filterTagsTeleport"
    />
</template>

<script lang="ts" setup>
import {
    computed, nextTick, onBeforeUnmount, ref, shallowRef, Slots, triggerRef, useSlots,
    watch,
} from 'vue';
import dayjs from 'dayjs';
import Checkbox from 'primevue/checkbox';
import Popover from 'primevue/popover';
import PrimeButton from 'primevue/button';
import { useRoute, useRouter } from 'vue-router';
import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IGenericTableHeaderOverrides } from '@/interfaces/powerMarkets/table/IGenericTableHeaderOverrides.ts';
import { IGenericTableHeaderRow, IGenericTableParentHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';
import { IGenericTableRowData } from '@/interfaces/powerMarkets/table/IGenericTableRowData.ts';
import { IGenericTableRowFormatting } from '@/interfaces/powerMarkets/table/IGenericTableRowFormatting.ts';
import {
    IDateHeadingFilters,
    IHeadingFilters,
    INumericHeadingFilters,
    ITextHeadingFilters,
} from '@/interfaces/powerMarkets/table/IHeadingFilters.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { TimeFormatEnum } from '@/enums/powerMarkets/TimeFormatEnum.ts';
import { ITableHeaderToSort, IUserTableFilters } from '@/interfaces/powerMarkets/table/ITableFilters.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useBingoStore } from '@/stores/powerMarkets/bingoStore.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import EditTableColumns from '@/components/powerMarkets/overlays/EditTableColumns.vue';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import TableDownloadTypeSelect from '@/components/powerMarkets/overlays/TableDownloadTypeSelect.vue';
import { convertDoubleToDuration } from '@/helpers/table/convertDoubleToDuration.ts';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import ConditionalTeleport from '@/components/layout/ConditionalTeleport.vue';
import GenericHeaderFilter from '@/components/powerMarkets/tables/GenericHeaderFilter.vue';
import GenericTableFilterTags from '@/components/powerMarkets/tables/GenericTableFilterTags.vue';
import { useUserSettingsActions } from '@/actions/powerMarkets/userSettingsActions.ts';
import { Messages } from '@/constants/Messages.ts';
import { escapeHtml } from '@/utils/htmlUtils.ts';
import PlantSwitch from '@/components/powerMarkets/plant/PlantSwitch.vue';
import { IDateRange } from '@/interfaces/general/IDateRange.ts';
import { debounce } from '@/utils/Debounce.ts';
import { useToast } from 'primevue/usetoast';
import { publishToast } from '@/utils/Toast.ts';
import { formatGenericTableDateValue, getUnixBoundsFromTableDateFilterRange } from '@/utils/dateUtils.ts';
import { capitaliseString } from '@/utils/stringUtils.ts';
import type { PlantDetailsTab } from '@/constants/powerMarkets/plantDetailsTabs.ts';
import type { RowClickMode } from '@/types/table/IRowClickMode.ts';
import { normalizePlantIdForModal } from '@/helpers/table/normalizePlantIdForModal.ts';
import { getSelectedPlantGroupColourSettings, getSelectedPlantIds } from '@/helpers/powerMarkets/plantGroupSettings.ts';
import { getColumnAlignments } from '@/helpers/table/columnAlignment.ts';
import { getTableGridColumn, getTableGridPlacement, getTableHeaderGridRow } from '@/helpers/table/tableGridPlacement.ts';
import {
    createDefaultFilterProperty,
    getSampleCellForHeader,
    getUniqueValuesForHeader,
} from '@/helpers/table/tableFilterColumnsFromData.ts';
import { useFixedTableColumns } from './useFixedTableColumns.ts';

const userSettingsStore = useUserSettingsStore();
const bingoStore = useBingoStore();
const userSettingsActions = useUserSettingsActions();
const route = useRoute();
const router = useRouter();
const dataStore = useDataStore();
const toast = useToast();

const slots: Slots = useSlots();

const props = defineProps<{
    data: IGenericTableData;
    preventDownload?: boolean;
    filename?: string;
    header?: string;
    noDataFoundMessage?: string;
    smallCells?: boolean;
    smallHeader?: boolean;
    checkable?: boolean;
    isLoading?: boolean;
    rowClickMode?: RowClickMode;
    disableReorder?: boolean;
    hideTopFilters?: boolean;
    optionsTeleport?: HTMLElement | null;
    editTableTeleport?: HTMLElement | null;
    editTableClick?:(event: Event) => void;
    filterTagsTeleport?: HTMLElement | null;
    backendFilters?: IUserTableFilters | null;
    hardcodedTableFilters?: IUserTableFilters | null;
    sortPrecedence?: { id: string; ascending: boolean } | null;
    customSort?:((tableData: Array<IGenericTableRowData>) => Array<IGenericTableRowData>) | null;
    customSortControlHeaderIds?: Array<string>;
    syncSortFromData?: boolean;
    userTableFilterId?: string | null;
    hidePoundDecimals?: boolean;
    showEntryOverride?: boolean;
    isFixedModule?: boolean;
    plantModalDateRange?: IDateRange;
    initialPlantDetailsTab?: PlantDetailsTab;
    cannotEdit?: boolean;
    hideButtonRow?: boolean;
    noPlantIdOverride?: boolean;
    inverted?: boolean;
    hoveredRowId?: string | null;
    /** When set, selection is controlled by the parent and must not be read back from row.selected in props.data. */
    selectedRowIds?: Array<string> | null;
    /** Change when switching datasets without remounting the table. */
    datasetKey?: string | number;
}>();

const emit = defineEmits<{
    'update:filters': [Record<string, IHeadingFilters>];
    'update:table-filters': [IUserTableFilters];
    'update:selected-rows': [Array<IGenericTableRowData>];
    'select:row': [IGenericTableRowData];
    'update:shown-count': [number];
    'update:displayed-rows': [Array<IGenericTableRowData>];
}>();

const isSelectionControlled = computed<boolean>(() => props.selectedRowIds != null);

const INITIAL_ROWS = 100;

type TOrderedRowCell = [
    cell: IGenericTableRowFormatting,
    header: IGenericTableHeaderRow,
    headerIndex: number,
    colspan: number,
];

const parentHeaders = shallowRef<Array<IGenericTableParentHeaderRow>>([]);
const tableHeaders = shallowRef<Array<IGenericTableHeaderRow>>([]);
const displayedTableHeaders = shallowRef<Array<IGenericTableHeaderRow>>([]);
/** Locally derived filter options; cleared on data refresh. API-supplied header.uniqueValues are never stored here. */
const derivedUniqueValuesByHeaderId = shallowRef<Record<string, Array<string> | Array<number>>>({});

const initialTableData = shallowRef<Array<IGenericTableRowData>>([]);
const displayedTableData = shallowRef<Array<IGenericTableRowData>>([]);

const showEditTableButton = computed<boolean>(() => !props.cannotEdit && initialTableData.value.length > 0);
const showDownloadTableButton = computed<boolean>(() => !props.preventDownload && displayedTableData.value.length > 0);
const showTableActionButtons = computed<boolean>(() => showEditTableButton.value || showDownloadTableButton.value);

const hasSlotHeader = ref<boolean>(!!slots.header);
const currentOpenHeaderToFilter = ref<string | null>(null);
const selectedHeaderToFilter = ref<IGenericTableHeaderRow | null>(null);
const selectedPropertyForFilter = ref<IGenericTableRowFormatting | null>(null);
const selectedRows = ref<Array<IGenericTableRowData>>([]);
const numberOfScrollDowns = ref<number>(0);
const tableWrapperRef = ref<HTMLElement | null>(null);
const tableElementRef = ref<HTMLTableElement | null>(null);

// plant modal
const showPlantModal = ref<boolean>(false);
const selectedPlantId = ref<string | null>(null);

// filters
const currentFiltersByHeading = ref<Record<string, IHeadingFilters>>({});
const columnsToHide = ref<Array<string>>([]);
const headerToSort = ref<ITableHeaderToSort>({ ascending: false, id: null });
const tableUnitsSetting = ref<string>('inherit');
const orderedHeaders = ref<Array<string>>([]);

// popover refs
const headerSettingsOverlay = ref<InstanceType<typeof Popover> | null>(null);
const editColumnsOverlay = ref<InstanceType<typeof Popover> | null>(null);
const downloadOverlay = ref<InstanceType<typeof Popover> | null>(null);
const tooltipOverlay = ref<InstanceType<typeof Popover> | null>(null);

// exposed table downloader
const excelDownloadComponentRef = ref();

// tooltip
const tooltipText = ref<string | null>(null);
const currentCellKey = ref<string | null>(null);
const tooltipActive = ref<boolean>(false);
function showTooltip (): void {
    tooltipActive.value = true;
}

function hideTooltip (): void {
    tooltipActive.value = false;
    tooltipText.value = null;
    currentCellKey.value = null;
}

const updateTooltipDebounced = debounce(updateTooltip, 50);

async function updateTooltip (event: MouseEvent): Promise<void> {
    if (!tooltipOverlay.value || !tooltipActive.value) return;

    const target = (event.target as HTMLElement).closest('[data-tooltip]') as HTMLElement;

    if (target) {
        const tooltip = target.getAttribute('data-tooltip');
        const parentCell = target.closest('td[data-cell-key]') as HTMLElement | null;
        const cellKey = target.getAttribute('data-cell-key') ?? parentCell?.getAttribute('data-cell-key');

        if (tooltip && (cellKey !== currentCellKey.value || tooltip !== tooltipText.value)) {
            tooltipOverlay.value.hide();
            await nextTick();
            tooltipText.value = tooltip;
            currentCellKey.value = cellKey ?? null;
            tooltipOverlay.value.show(event, target);
        }
    } else {
        tooltipText.value = null;
        currentCellKey.value = null;
        tooltipOverlay.value.hide();
    }
}

// computeds
const userSettings = computed<ISiteSettings>(() => userSettingsStore.userSettings.otherSettings.data);
const selectedPlantIds = computed<Array<string>>(() => getSelectedPlantIds(userSettings.value));
const selectedPlantGroupColourSettings = computed(() => getSelectedPlantGroupColourSettings(userSettings.value));
const timeFormat = computed<string>(() => (userSettings.value.timeFormatValue === TimeFormatEnum.TwelveHour ? 'h:mm a' : 'H:mm'));
const chosenTableUnitsSettings = computed<string>(() => (tableUnitsSetting.value === 'inherit' ? userSettings.value.tableUnitValue : tableUnitsSetting.value));
const downloadFileName = computed<string>(() => `${props.filename ?? (props.data.tabName || 'Table download')} - downloaded at ${dayjs().format(`D MMM YYYY, ${timeFormat.value}`)}`);
const numberOfShownRows = computed<number>(() => Math.min(displayedTableData.value.length, INITIAL_ROWS + 50 * numberOfScrollDowns.value));

const visibleTableRows = computed<Array<IGenericTableRowData>>(() => {
    const total = displayedTableData.value.length;
    const shown = numberOfShownRows.value;

    if (!props.inverted) {
        return displayedTableData.value.slice(0, shown);
    }

    return displayedTableData.value.slice(Math.max(0, total - shown), total);
});

const columnAlignments = computed(() => getColumnAlignments(
    visibleTableRows.value,
    displayedTableHeaders.value.map(header => header.id),
));

const tableGrid = computed(() => getTableGridPlacement(
    displayedTableHeaders.value.map(header => header.id),
    parentHeaders.value.map(header => header.columnSpan),
    !!props.checkable,
));

const parentHeaderCells = computed(() => parentHeaders.value.flatMap((header, index) => {
    const standalone = displayedTableHeaders.value.find(leaf => leaf.id === header.id && !leaf.parentHeaderId);
    if (standalone && standalone.rowSpan > 1) return [];
    return [{ header, index, gridColumn: tableGrid.value.parentColumns[index] }];
}));

const tableLayoutReady = computed(() => !props.isLoading && visibleTableRows.value.length > 0);
const { columns: fixedGridColumns, resetColumns, refitColumns } = useFixedTableColumns(tableElementRef, tableLayoutReady);

watch(
    [
        (): string | number | undefined => props.datasetKey,
        (): string => props.data.tabName,
        (): string => props.data.type,
        (): string | undefined => props.data.country,
        (): boolean | undefined => props.checkable,
        (): string => chosenTableUnitsSettings.value,
    ],
    resetColumns,
    { flush: 'post' },
);

watch(
    () => displayedTableHeaders.value.map(header => header.id),
    (ids, previousIds) => {
        if (ids.length !== previousIds.length || ids.some((id, index) => id !== previousIds[index])) resetColumns();
    },
    { flush: 'post' },
);

watch(
    () => displayedTableHeaders.value.map(header => ({
        id: header.id,
        filtered: headerHasFilter(header.id),
        sorted: headerToSort.value.id === header.id,
    })),
    (headers, previousHeaders) => {
        if (headers.length !== previousHeaders.length || headers.some((header, index) => header.id !== previousHeaders[index].id)) return;
        const changedColumns = headers.flatMap((header, index) => (
            header.filtered !== previousHeaders[index].filtered || header.sorted !== previousHeaders[index].sorted
                ? [tableGrid.value.startById[header.id]] : []
        ));
        refitColumns(changedColumns);
    },
    { flush: 'post' },
);

function scrollToBottom (): void {
    if (!tableWrapperRef.value) return;
    tableWrapperRef.value.scrollTop = tableWrapperRef.value.scrollHeight;
}

function resetVirtualScroll (): void {
    numberOfScrollDowns.value = 0;
}

function getParentHeaderTitle (header: IGenericTableParentHeaderRow): string {
    if (chosenTableUnitsSettings.value === 'header' && parentHeaderSuffixesPrefixes.value[header.id] !== '') {
        return `${header.title} (${parentHeaderSuffixesPrefixes.value[header.id]})`;
    }
    return header.title;
}

function getHeaderTitle (header: IGenericTableHeaderRow, headerNo: number): string {
    if (chosenTableUnitsSettings.value === 'header'
    && !header.multipleUnits
    && headerSuffixesPrefixes.value[headerNo] !== '') {
        return `${header.title} (${headerSuffixesPrefixes.value[headerNo]})`;
    }
    return header.title;
}

const allHeaderSuffixesPrefixes = computed(() => displayedTableHeaders.value.map((header, indexOf) => {
    const headerSuffixPrefix = headerSuffixesPrefixes.value[indexOf];
    if (headerSuffixPrefix) return headerSuffixPrefix;
    if (header.parentHeaderId) return parentHeaderSuffixesPrefixes.value[header.parentHeaderId];
    return '';
}));

const headerSuffixesPrefixes = computed(() => displayedTableHeaders.value.map(header => getPrefixSuffixAdder(header)));
const parentHeaderSuffixesPrefixes = computed(() => Object.fromEntries(parentHeaders.value.map(header => [header.id, getParentPrefixSuffixAdder(header)])));

function getParentPrefixSuffixAdder (header: IGenericTableParentHeaderRow): string {
    let prefix = '';
    let suffix = '';

    if (initialTableData.value.length) {
        let colFound: IGenericTableRowFormatting | null = null;
        let headerFound: IGenericTableHeaderRow | null = null;
        for (let i = 0; i < initialTableData.value.length; i += 1) {
            const keyFound = Object.keys(initialTableData.value[i].row).find(key => key.includes(header.id));
            colFound = keyFound ? initialTableData.value[i].row[keyFound] : null;
            headerFound = tableHeaders.value.find(h => h.id === keyFound) ?? null;
            if (colFound) break;
        }

        if (colFound && headerFound) {
            const colFoundPrefix = getHeaderOverride(colFound, 'prefix', headerFound);
            const colFoundSuffix = getHeaderOverride(colFound, 'suffix', headerFound);

            const headerShowUnits = Object.keys(initialTableData.value[0].row).every(key => {
                const value = initialTableData.value[0].row[key];
                const thisKeyHeader = tableHeaders.value.find(h => h.id === key);
                return !thisKeyHeader || thisKeyHeader.parentHeaderId !== header.id
                || (colFoundPrefix === getHeaderOverride(value, 'prefix', thisKeyHeader)
                && colFoundSuffix === getHeaderOverride(value, 'suffix', thisKeyHeader));
            });
            if (headerShowUnits) {
                prefix = colFoundPrefix ? colFoundPrefix as string : '';
                suffix = colFoundSuffix ? colFoundSuffix as string : '';
            }
        }
    }

    return prefix === '' && suffix === '' ? '' : `${prefix}${suffix}`;
}

function getPrefixSuffixAdder (header: IGenericTableHeaderRow): string {
    let prefix = '';
    let suffix = '';

    if (header.prefixOverrideWhenInHeader || header.suffixOverrideWhenInHeader) {
        prefix = header.prefixOverrideWhenInHeader || '';
        suffix = header.suffixOverrideWhenInHeader || '';
    } else if (initialTableData.value.length) {
        let colFound: IGenericTableRowFormatting | null = null;
        let headerFound: IGenericTableHeaderRow | null = null;
        for (let i = 0; i < initialTableData.value.length; i += 1) {
            const keyFound = Object.keys(initialTableData.value[i].row).find(key => key.includes(header.id));
            colFound = keyFound ? initialTableData.value[i].row[keyFound] : null;
            headerFound = tableHeaders.value.find(h => h.id === keyFound) ?? null;
            if (colFound) break;
        }

        if (colFound && headerFound) {
            const colFoundPrefix = getHeaderOverride(colFound, 'prefix', headerFound);
            const colFoundSuffix = getHeaderOverride(colFound, 'suffix', headerFound);

            const headerShowUnits = Object.keys(initialTableData.value[0].row).some(key => {
                const value = initialTableData.value[0].row[key];
                const thisKeyHeader = tableHeaders.value.find(h => h.id === key);
                return thisKeyHeader && thisKeyHeader.parentHeaderId === header.parentHeaderId
                && (colFoundPrefix !== getHeaderOverride(value, 'prefix', thisKeyHeader)
                || colFoundSuffix !== getHeaderOverride(value, 'suffix', thisKeyHeader));
            });
            if (headerShowUnits) {
                prefix = colFoundPrefix ? colFoundPrefix as string : '';
                suffix = colFoundSuffix ? colFoundSuffix as string : '';
            }
        }
    }

    return prefix === '' && suffix === '' ? '' : `${prefix}${suffix}`;
}

function getHeaderOverride (
    item: IGenericTableRowFormatting,
    propertyName: keyof IGenericTableHeaderOverrides,
    header: IGenericTableHeaderRow,
): string | boolean | null {
    const overrideValue = item.headerOverrides?.[propertyName];
    if (overrideValue != null && overrideValue !== '') return overrideValue;
    return header[propertyName] ?? null;
}

function isColumnSpanHidden (cell: IGenericTableRowFormatting): boolean {
    return cell.cellFormatProperties?.hideCell === true;
}

function getCellColSpan (cell: IGenericTableRowFormatting, headerIndex: number): number {
    const requestedSpan = cell.cellFormatProperties?.columnSpan ?? 1;
    if (requestedSpan <= 1) return 1;

    const currentHeaderId = displayedTableHeaders.value[headerIndex]?.id;
    if (!currentHeaderId) return requestedSpan;
    const allHeaderIds = tableHeaders.value.map(h => h.id);
    const startIndex = allHeaderIds.indexOf(currentHeaderId);
    if (startIndex === -1) return requestedSpan;

    const visibleSpan = allHeaderIds
        .slice(startIndex, startIndex + requestedSpan)
        .filter(id => !columnsToHide.value.includes(id))
        .length;
    return Math.max(1, visibleSpan);
}

function orderedRow (row: IGenericTableRowData): Array<TOrderedRowCell> {
    return displayedTableHeaders.value.flatMap((header, headerIndex) => {
        const cell = row.row[header.id];
        if (!cell || isColumnSpanHidden(cell)) return [];
        return [[cell, header, headerIndex, getCellColSpan(cell, headerIndex)]];
    });
}

function updateShownHeaders (): void {
    displayedTableHeaders.value = tableHeaders.value.filter(header => !columnsToHide.value.includes(header.id));

    if (!props.data.headerSecondRow?.length || !Object.keys(props.data.headerFirstRowById).length) {
        parentHeaders.value = [];
        return;
    }
    parentHeaders.value = displayedTableHeaders.value.reduce((accumulator: Array<IGenericTableParentHeaderRow>, header: IGenericTableHeaderRow) => {
        const lastAccumulator = accumulator[accumulator.length - 1];

        if (lastAccumulator && lastAccumulator.id === header.parentHeaderId) {
            lastAccumulator.columnSpan += 1;
        } else {
            const firstHeader = Object.values(props.data.headerFirstRowById).find(h => h.id === header.parentHeaderId);

            if (firstHeader && 'columnSpan' in firstHeader) {
                accumulator.push({ ...firstHeader, columnSpan: 1 } as IGenericTableParentHeaderRow);
            } else {
                accumulator.push({
                    title: '', id: header.id, hoverText: null, isHeaderParent: true, columnSpan: 1,
                });
            }
        }

        return accumulator;
    }, []);
}

function headerHasFilter (headerId: string): boolean {
    if (!(headerId in currentFiltersByHeading.value)) return false;
    return !currentFiltersByHeading.value[headerId].noFilter;
}

interface ITextFilterMatchConfig {
    searchSet: Set<string | null>;
    inverseSearch: boolean;
}

function getTextFilterMatchConfig (currentFilter: ITextHeadingFilters): ITextFilterMatchConfig | null {
    if (!currentFilter.options.values.length && !currentFilter.options.group) return null;
    let searchVals: Array<string | null> = [];
    let inverseSearch = currentFilter.options.exclude;
    if (!currentFilter.options.group) {
        searchVals = currentFilter.options.values;
    } else if (currentFilter.options.group === 'bingoEnact' || currentFilter.options.group === 'notBingoEnact') {
        searchVals = bingoStore.activeBingoPlantData.map(plantData => plantData.plantId);
        inverseSearch = inverseSearch || currentFilter.options.group === 'notBingoEnact';
    } else {
        searchVals = userSettings.value.chosenPlantsByGroup[currentFilter.options.group] ?? [];
    }
    return {
        searchSet: new Set(searchVals),
        inverseSearch,
    };
}

function cellMatchesTextFilter (
    cellValues: Array<number | string | null>,
    matchConfig: ITextFilterMatchConfig | null,
): boolean {
    if (!matchConfig) return true;
    const match = cellValues.some(v => matchConfig.searchSet.has(v as string | null));
    return matchConfig.inverseSearch ? !match : match;
}

function cellMatchesNumericFilter (cellValues: Array<number | string | null>, currentFilter: INumericHeadingFilters): boolean {
    let numericValues = cellValues
        .map((v) => {
            if (typeof v === 'number') return v;
            if (typeof v === 'string') {
                const parsedValue = parseFloat(v);
                return Number.isNaN(parsedValue) ? null : parsedValue;
            }
            return null;
        })
        .filter((v): v is number => v !== null);
    if (currentFilter.options.absolute) numericValues = numericValues.map(v => Math.abs(v));
    if (!numericValues.length) return false;

    const min = currentFilter.options.min ?? Math.min(...numericValues);
    const max = currentFilter.options.max ?? Math.max(...numericValues);

    return numericValues.some(v => v >= min && v <= max);
}

function cellMatchesDateFilter (cellValues: Array<number | string | null>, currentFilter: IDateHeadingFilters): boolean {
    const dateTimes = cellValues.filter(v => typeof v !== 'string' && v !== null) as Array<number>;
    const bounds = getUnixBoundsFromTableDateFilterRange(currentFilter.options.date?.dateRange);
    const from = bounds?.from ?? Math.min(...dateTimes);
    const to = bounds?.to ?? Math.max(...dateTimes);
    return dateTimes.some(v => v >= from && v <= to);
}

function getRowHeaderKeyForFilter (row: IGenericTableRowData, filterHeaderKey: string): string | null {
    if (row.row[filterHeaderKey]) return filterHeaderKey;

    // Legacy saved filters can use a lowercased first letter from the old Enact DB format.
    const upperFirstLetterHeaderKey = capitaliseString(filterHeaderKey);
    if (row.row[upperFirstLetterHeaderKey]) return upperFirstLetterHeaderKey;

    return null;
}

function isHardcodedFilter (headerId: string, filterHeaderKey: string): boolean {
    const hardcodedFilters = props.hardcodedTableFilters?.currentFiltersByHeading;
    if (!hardcodedFilters) return false;
    return headerId in hardcodedFilters || filterHeaderKey in hardcodedFilters;
}

function shouldApplyFilterForHeader (rowHeaderKey: string, filterHeaderKey: string): boolean {
    const header = tableHeaders.value.find(h => h.id === rowHeaderKey);
    if (!header) return true;
    return header.allowFilter || isHardcodedFilter(header.id, filterHeaderKey);
}

async function fetchBingoDataIfNeeded (): Promise<void> {
    const hasBingoGroup = Object.values(currentFiltersByHeading.value).some(filter => filter.type === 'text'
        && (filter.options.group === 'bingoEnact' || filter.options.group === 'notBingoEnact'));
    if (hasBingoGroup && !bingoStore.activeBingoPlantData.length) {
        try {
            await bingoStore.refreshActiveBingoPlantData(bingoStore.activeBingoBoardPlantIds);
        } catch {
            publishToast(toast, 'Unable to filter plants by bingo group', 'error');
        }
    }
}

async function filterTableData (): Promise<void> {
    await fetchBingoDataIfNeeded();

    const textFilterMatchConfigs = new Map<string, ITextFilterMatchConfig | null>();
    Object.entries(currentFiltersByHeading.value).forEach(([header, currentFilter]) => {
        if (currentFilter.type === 'text') {
            textFilterMatchConfigs.set(header, getTextFilterMatchConfig(currentFilter));
        }
    });

    const filteredData = initialTableData.value.filter(row => {
        if (row.hideRow) return false;
        return Object.keys(currentFiltersByHeading.value).every(header => {
            // Check a saved filter doesn't exist anymore
            const rowHeaderKey = getRowHeaderKeyForFilter(row, header);
            if (!rowHeaderKey) {
                delete currentFiltersByHeading.value[header];
                return true;
            }

            if (!shouldApplyFilterForHeader(rowHeaderKey, header)) return true;

            const cellValues = row.row[rowHeaderKey].values;
            const currentFilter = currentFiltersByHeading.value[header];

            if (currentFilter.type === 'text') {
                return cellMatchesTextFilter(cellValues, textFilterMatchConfigs.get(header) ?? null);
            }

            if (currentFilter.type === 'numeric') {
                return cellMatchesNumericFilter(cellValues, currentFilter);
            }

            if (currentFilter.type === 'date') {
                return cellMatchesDateFilter(cellValues, currentFilter);
            }

            return true;
        });
    });
    displayedTableData.value = sortTableData(filteredData);
    emit('update:shown-count', displayedTableData.value.length);
    emit('update:displayed-rows', displayedTableData.value);
}

function isNullishSortValue (value: string | number | null | undefined): boolean {
    if (value == null) return true;
    if (typeof value === 'string' && (value.trim() === '' || value.trim() === '-')) return true;
    return false;
}

function tableValueSort (a: string | number | null | undefined, b: string | number | null | undefined, sortHeader: string): number {
    const aNullish = isNullishSortValue(a);
    const bNullish = isNullishSortValue(b);

    if (aNullish && bNullish) return 0;
    if (props.inverted) {
        if (aNullish) return -1;
        if (bNullish) return 1;
    } else {
        if (aNullish) return 1;
        if (bNullish) return -1;
    }

    if (typeof a === 'number' && typeof b === 'number') {
        return headerToSort.value.ascending ? a - b : b - a;
    }

    // Add other date formats here as they come up
    const aDate = dayjs(a, ['MM/YYYY']);
    const bDate = dayjs(b, ['MM/YYYY']);

    if (aDate.isValid() && bDate.isValid()) {
        return headerToSort.value.ascending ? aDate.valueOf() - bDate.valueOf() : bDate.valueOf() - aDate.valueOf();
    }

    if (typeof a === 'string' && typeof b === 'string') {
        if (sortHeader.toLowerCase().includes('zone')) {
            const numA = parseInt(a.substring(1), 10);
            const numB = parseInt(b.substring(1), 10);
            return headerToSort.value.ascending ? numA - numB : numB - numA;
        }
        return headerToSort.value.ascending ? a.localeCompare(b) : b.localeCompare(a);
    }
    return 0;
}

function sortTableData (tableData: Array<IGenericTableRowData>): Array<IGenericTableRowData> {
    if (!tableData.length) return tableData;

    if (!tableHeaders.value.find(h => h.id === headerToSort.value.id)) {
        headerToSort.value.id = props.data.headerToSort;
        headerToSort.value.ascending = props.data.ascending ?? false;
    }

    if (props.customSort) return props.customSort(tableData);

    if (!headerToSort.value.id || !tableHeaders.value.find(h => h.id === headerToSort.value.id)) return tableData;
    const sortHeader = headerToSort.value.id;

    return tableData.toSorted((a, b) => {
        if (props.sortPrecedence?.id && a.row[props.sortPrecedence.id] && b.row[props.sortPrecedence.id]) {
            const aPriorityValue = a.row[props.sortPrecedence.id].values[0];
            const bPriorityValue = b.row[props.sortPrecedence.id].values[0];
            if (typeof aPriorityValue === 'number' && typeof bPriorityValue === 'number' && aPriorityValue !== bPriorityValue) {
                return props.sortPrecedence.ascending ? aPriorityValue - bPriorityValue : bPriorityValue - aPriorityValue;
            }
        }

        const aCell = a.row[sortHeader];
        const bCell = b.row[sortHeader];
        const aValue = aCell?.cellFormatProperties?.sortValue ?? aCell?.values[0];
        const bValue = bCell?.cellFormatProperties?.sortValue ?? bCell?.values[0];

        return tableValueSort(aValue, bValue, sortHeader);
    });
}

function getIcons (cell: IGenericTableRowFormatting) : Array<{ icon: string; colour: string; tooltip: string | null }> {
    const icons: Array<{ icon: string; colour: string; tooltip: string | null }> = [];
    const valueToIcons = cell.valueToIcons;
    if (!valueToIcons) return icons;

    Object.keys(valueToIcons).forEach((cellValue) => {
        cell.values.forEach((toFind) => {
            if (!toFind || typeof toFind === 'number' || toFind.toLowerCase() !== cellValue.toLowerCase()) return;
            icons.push({
                icon: valueToIcons[cellValue] ?? '',
                colour: cell.valueToColours?.[cellValue] ?? '',
                tooltip: cell.valueToTooltips?.[cellValue] ?? null,
            });
        });
    });
    return icons;
}

function formatCellText (cell: IGenericTableRowFormatting, suffixPrefix: boolean, isExcel: boolean, header: IGenericTableHeaderRow): string {
    if (shouldHideCellEntry(cell, isExcel)) {
        return cell.directionIcon ? getDirectionIcon(cell) : '-';
    }

    let valuesToConsider = [];
    if (cell.displayValues?.length) {
        valuesToConsider = cell.displayValues;
    } else if (props.showEntryOverride && cell.cellFormatProperties && cell.cellFormatProperties.alternativeHiddenString) {
        valuesToConsider.push(cell.cellFormatProperties.alternativeHiddenString);
    } else {
        valuesToConsider = cell.values;
    }
    if (valuesToConsider == null || valuesToConsider[0] == null) return '-';
    const directionIcon = isExcel ? getDirectionIcon(cell) : '';
    if (cell.displayValues?.length) {
        return valuesToConsider.map(value => (typeof value === 'string' ? escapeHtml(value) : formatIndividualCellValue(value, cell, suffixPrefix, isExcel, header))).join(', ') + directionIcon;
    }
    if (valuesToConsider.length === 1) return formatIndividualCellValue(valuesToConsider[0], cell, suffixPrefix, isExcel, header) + directionIcon;

    const formattedValues = valuesToConsider.map(value => formatIndividualCellValue(value, cell, suffixPrefix, isExcel, header));
    if (getHeaderOverride(cell, 'ignoreBrackets', header)) return formattedValues.join(', ') + directionIcon;

    return `${formattedValues[0]} (${formattedValues.slice(1).join(', ')})${directionIcon}`;
}

function shouldRenderDirectionIcon (cell: IGenericTableRowFormatting): boolean {
    return !!cell.directionIcon && !shouldHideCellEntry(cell, false);
}

function getDirectionIcon (cell: IGenericTableRowFormatting): string {
    return cell.directionIcon?.trim() ?? '';
}

function shouldHideCellEntry (cell: IGenericTableRowFormatting, isExcel: boolean): boolean {
    return !props.showEntryOverride && cell.hideEntry && !isExcel;
}

function formatIndividualCellValue (value: string | number | null, cell: IGenericTableRowFormatting, suffixPrefix: boolean, isExcel: boolean, header: IGenericTableHeaderRow): string {
    if (value == null) return '-';
    if (cell.cellType === 'text') {
        if (header.isPlant && !props.noPlantIdOverride) return dataStore.getPlantNameById(value.toString()) || '-';
        return value.toString() || '-';
    }
    if (cell.cellType === 'number') {
        if (typeof value !== 'number') return '-';
        if (getHeaderOverride(cell, 'convertCellValueToDuration', header)) return convertDoubleToDuration(value);

        const showUnits = chosenTableUnitsSettings.value === 'cell' || (chosenTableUnitsSettings.value === 'header' && getHeaderOverride(cell, 'showUnitsEvenWhenInHeader', header));
        const prefix = getHeaderOverride(cell, 'prefix', header) || header.prefixOverrideWhenInHeader;
        const suffix = getHeaderOverride(cell, 'suffix', header) || header.suffixOverrideWhenInHeader;

        return `${
            showUnits && prefix && suffixPrefix ? prefix : ''
        }${suffixPrefix ? formatNumber(value, cell, header) : value}${
            showUnits && suffix && suffixPrefix ? suffix : ''
        }`;
    }
    if (cell.cellType === 'date') {
        if (typeof value !== 'number') return '-';
        return formatGenericTableDateValue(value, cell.dateFormat, timeFormat.value, { isExcel });
    }
    return '-';
}

function formatNumber (value: number, cell: IGenericTableRowFormatting, header: IGenericTableHeaderRow): string {
    let num: string | number = value;

    if (props.hidePoundDecimals && getHeaderOverride(cell, 'prefix', header) === '£') num = Math.round(num);
    else num = Math.round((num + Number.EPSILON) * 100) / 100;

    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

function getSortingIcon (header: string): string {
    const sampleCell = getSampleCellForHeader(initialTableData.value, header);
    if (sampleCell?.cellType === 'number') {
        return headerToSort.value.ascending ? 'fa-arrow-down-1-9' : 'fa-arrow-down-9-1';
    }

    return headerToSort.value.ascending ? 'fa-arrow-down-a-z' : 'fa-arrow-down-z-a';
}

function headerSticksLeft (header: IGenericTableHeaderRow): boolean {
    const firstRow = displayedTableData.value.find(row => row.row[header.id]);
    if (!firstRow) return false;
    return firstRow.row[header.id].freeze;
}

function headerHasBorder (header: IGenericTableHeaderRow, index: number): boolean {
    if (!parentHeaders.value || index >= displayedTableHeaders.value.length - 1) return false;
    return header.parentHeaderId !== displayedTableHeaders.value[index + 1].parentHeaderId;
}

function headerShouldHideRightBorder (index: number): boolean {
    const header = displayedTableHeaders.value[index];
    if (!header) return false;

    const headerIsBlank = getHeaderTitle(header, index).trim() === '';
    if (headerIsBlank) return index < displayedTableHeaders.value.length - 1;

    const nextHeader = displayedTableHeaders.value[index + 1];
    return index === displayedTableHeaders.value.length - 2
        && nextHeader !== undefined
        && getHeaderTitle(nextHeader, index + 1).trim() === '';
}

function getHeaderStyle (header: IGenericTableHeaderRow): Record<string, string> {
    const styles: Record<string, string> = {
        gridColumn: getTableGridColumn(tableGrid.value.startById[header.id]),
        gridRow: getTableHeaderGridRow(header.rowSpan, !!parentHeaders.value.length),
    };

    if (header.minWidth) styles['--column-min-width'] = header.minWidth;

    return styles;
}

function getCellStyle (cell: IGenericTableRowFormatting, headerIndex: number, colspan: number): Record<string, string> {
    const styles: Record<string, string> = {};
    const header = displayedTableHeaders.value[headerIndex];

    styles.textAlign = columnAlignments.value[header.id];
    styles.gridColumn = getTableGridColumn(tableGrid.value.startById[header.id], colspan);

    if (cell.textColour) styles.color = cell.textColour;
    if (cell.cellFormatProperties?.borderRight) styles.borderRight = cell.cellFormatProperties.borderRight;
    if (cell.cellFormatProperties?.border) styles.border = cell.cellFormatProperties.border;
    if (cell.cellFormatProperties?.textAlign) styles.textAlign = cell.cellFormatProperties.textAlign;
    styles['--column-justify'] = styles.textAlign;

    let backgroundColour = cell.backgroundColour;
    const colourIdOverride = cell.cellFormatProperties?.colourIdOverride;

    if (colourIdOverride && colourIdOverride in userSettings.value.bingoColourOverrides) {
        backgroundColour = userSettings.value.bingoColourOverrides[colourIdOverride];
    } else if (getHeaderOverride(cell, 'isPlant', displayedTableHeaders.value[headerIndex])) {
        if (selectedPlantGroupColourSettings.value.override
            && typeof cell.values[0] === 'string'
            && selectedPlantIds.value.includes(cell.values[0])
        ) {
            backgroundColour = `${selectedPlantGroupColourSettings.value.colour} !important`;
        }
    }

    if (backgroundColour) styles.backgroundColor = backgroundColour;

    if (shouldHideCellEntry(cell, false) && !cell.directionIcon && backgroundColour) {
        styles.color = backgroundColour;
    }

    if (styles.backgroundColor && !styles.color) {
        styles.color = 'white';
    }
    return styles;
}

function getScaledPercent (cell: IGenericTableRowFormatting, headerIndex: number): number {
    if (cell.percentScale !== 'visible-column-max') return cell.percent;

    const headerId = displayedTableHeaders.value[headerIndex]?.id;
    if (!headerId) return cell.percent;

    const max = displayedTableData.value.reduce((largest, row) => {
        const rowPercent = Math.abs(row.row[headerId]?.percent ?? 0);
        return rowPercent > largest ? rowPercent : largest;
    }, 0);

    if (!max) return 0;
    return Math.min(100, (Math.abs(cell.percent) / max) * 100);
}

function getPercentStyle (cell: IGenericTableRowFormatting, headerIndex: number): Record<string, string> {
    return {
        width: `${getScaledPercent(cell, headerIndex)}%`,
        backgroundColor: cell.percentColour ?? 'green',
        left: cell.percentAlign === 'right' ? 'auto' : '0',
        right: cell.percentAlign === 'right' ? '0' : 'auto',
    };
}

function formatDataToJson (): Array<Record<string, string>> {
    const data: Array<Record<string, string>> = [];
    const excelHeaders = displayedTableHeaders.value.filter(header => {
        const cells = displayedTableData.value.map(row => row.row[header.id]).filter(Boolean);
        if (!cells.length) return !header.noExcel;
        return cells.some(cell => !getHeaderOverride(cell, 'noExcel', header));
    });

    if (!displayedTableData.value) return data;

    displayedTableData.value.forEach(row => {
        data.push(Object.fromEntries(excelHeaders.map(header => {
            const cell = row.row[header.id];
            return [
                header.id.replace('.', '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim(),
                !cell || isColumnSpanHidden(cell) || getHeaderOverride(cell, 'noExcel', header)
                    ? ''
                    : formatCellText(cell, false, true, header),
            ];
        })));
    });

    return data;
}

function showEditTableOverlay (event: Event): void {
    if (props.editTableClick) {
        props.editTableClick(event);
        return;
    }

    editColumnsOverlay.value?.show(event);
}

function hideEditTableOverlay (): void {
    editColumnsOverlay.value?.hide();
}

function openDownloadTableOverlay (event: Event): void {
    downloadOverlay.value?.show(event);
}

function closeDownloadTableOverlay (): void {
    downloadOverlay.value?.hide();
}

function externalDownloadExcel (): void {
    if (!excelDownloadComponentRef.value) return;
    excelDownloadComponentRef.value.$el.click?.();
}

function haveSameSelectedUniqueIds (
    previousRows: Array<IGenericTableRowData>,
    nextRows: Array<IGenericTableRowData>,
): boolean {
    if (previousRows.length !== nextRows.length) return false;

    const previousIds = new Set(previousRows.map(row => row.uniqueId).filter((id): id is string => !!id));
    if (previousIds.size !== nextRows.length) return false;

    return nextRows.every(row => !!row.uniqueId && previousIds.has(row.uniqueId));
}

function applyExternalSelection (selectedIds: Array<string>): void {
    const selectedIdSet = new Set(selectedIds);
    const nextSelectedRows: Array<IGenericTableRowData> = [];

    initialTableData.value.forEach((row) => {
        const isSelected = !row.hideRow && !!row.uniqueId && selectedIdSet.has(row.uniqueId);
        row.selected = isSelected;
        if (isSelected) {
            nextSelectedRows.push(row);
        }
    });

    displayedTableData.value.forEach((row) => {
        row.selected = !row.hideRow && !!row.uniqueId && selectedIdSet.has(row.uniqueId);
    });

    selectedRows.value = nextSelectedRows;
    triggerRef(displayedTableData);
}

function toggleRow (selectedRow: IGenericTableRowData): void {
    selectedRow.selected = !selectedRow.selected;
    triggerRef(displayedTableData);

    const selectedRowIndex = selectedRows.value.findIndex(row => row.uniqueId === selectedRow.uniqueId);
    if (selectedRowIndex === -1) {
        selectedRows.value.push(selectedRow);
    } else {
        selectedRows.value.splice(selectedRowIndex, 1);
    }

    emit('update:selected-rows', [...selectedRows.value]);
}

function syncSelectedRowsFromTableData (): void {
    if (!props.checkable) return;

    if (isSelectionControlled.value) {
        applyExternalSelection(props.selectedRowIds ?? []);
        return;
    }

    const nextSelectedRows = initialTableData.value.filter(row => !row.hideRow && row.selected);
    const selectionIdsChanged = !haveSameSelectedUniqueIds(selectedRows.value, nextSelectedRows);
    const selectedRowInstancesChanged = selectedRows.value.length !== nextSelectedRows.length
        || selectedRows.value.some((row, index) => row !== nextSelectedRows[index]);

    selectedRows.value = nextSelectedRows;

    if (selectionIdsChanged || selectedRowInstancesChanged) {
        emit('update:selected-rows', [...selectedRows.value]);
    }
}

function selectRow (row: IGenericTableRowData): void {
    if (props.rowClickMode === 'disabled') return;

    if (props.checkable) {
        toggleRow(row);
        return;
    }

    if (row.clickEventUrl && !row.clickEventUrl.includes('plant')) {
        router.push(route.matched[route.matched.length - 2].path + row.clickEventUrl);
    }

    if (props.rowClickMode !== 'emit') {
        const plantHeader = tableHeaders.value.find(header => header.isPlant);
        const headerKey = plantHeader?.id ?? Object.keys(row.row).find(key => row.row[key].headerOverrides?.isPlant);

        if (headerKey) {
            const cell = row.row[headerKey];
            const alternativeHiddenString = cell.cellFormatProperties?.alternativeHiddenString;
            const rawPlantId = alternativeHiddenString || cell.values[0] as string || null;
            selectedPlantId.value = rawPlantId ? normalizePlantIdForModal(rawPlantId) : null;
            showPlantModal.value = true;
            return;
        }
    }

    emit('select:row', row);
}

function scrollTable (event: Event): void {
    const scrollTarget = event.target as HTMLElement | null;
    if (!scrollTarget) return;

    if (numberOfShownRows.value >= displayedTableData.value.length) return;

    if (!props.inverted) {
        if (scrollTarget.scrollTop + scrollTarget.clientHeight + 10 < scrollTarget.scrollHeight) return;
        numberOfScrollDowns.value += 1;
        return;
    }

    if (scrollTarget.scrollTop > 10) return;
    numberOfScrollDowns.value += 1;
}

async function openHeaderFilterOverlay (event: Event, header: IGenericTableHeaderRow): Promise<void> {
    if (!headerSettingsOverlay.value) return;

    const sampleCell = getSampleCellForHeader(initialTableData.value, header.id);
    const cellType = sampleCell?.cellType === 'number' ? 'number' : 'text';
    selectedPropertyForFilter.value = sampleCell ?? createDefaultFilterProperty(cellType);
    const cachedHeader = tableHeaders.value.find(h => h.id === header.id) ?? header;
    const isPlant = cachedHeader.isPlant || sampleCell?.headerOverrides?.isPlant === true;
    let headerForFilter: IGenericTableHeaderRow = { ...cachedHeader, isPlant };
    // Prefer API-supplied uniqueValues; otherwise use/cache values derived from current rows.
    // Skip for plant columns — the plant filter UI does not use uniqueValues.
    if (!isPlant && !cachedHeader.uniqueValues?.length && cellType === 'text') {
        const cachedDerived = derivedUniqueValuesByHeaderId.value[header.id];
        if (cachedDerived?.length) {
            headerForFilter = { ...headerForFilter, uniqueValues: cachedDerived };
        } else {
            const derivedUniqueValues = getUniqueValuesForHeader(initialTableData.value, header.id, cellType);
            if (derivedUniqueValues?.length) {
                derivedUniqueValuesByHeaderId.value = {
                    ...derivedUniqueValuesByHeaderId.value,
                    [header.id]: derivedUniqueValues,
                };
                headerForFilter = { ...headerForFilter, uniqueValues: derivedUniqueValues };
            }
        }
    }
    selectedHeaderToFilter.value = headerForFilter;

    if (!currentOpenHeaderToFilter.value) {
        headerSettingsOverlay.value.show(event);
        currentOpenHeaderToFilter.value = header.id;
        return;
    }
    if (currentOpenHeaderToFilter.value !== header.id) {
        headerSettingsOverlay.value.hide();
        await nextTick();
        headerSettingsOverlay.value.show(event);
        currentOpenHeaderToFilter.value = header.id;
    }
}

function updateColumnOrder (newOrder: Array<IGenericTableHeaderRow>): void {
    tableHeaders.value = newOrder;
    orderedHeaders.value = newOrder.map(h => h.id);
    updateShownHeaders();
    saveFilters();
}

function updateUnitLocation (value: string): void {
    tableUnitsSetting.value = value;
    saveFilters();
}

async function resetHeaders (): Promise<void> {
    currentFiltersByHeading.value = {};
    headerToSort.value.id = props.data.headerToSort;
    headerToSort.value.ascending = props.data.ascending ?? false;
    await filterTableData();
    hideEditTableOverlay();
    saveFilters();
}

function resetCurrentHeaderOverlay (): void {
    flushSaveFilters();
    currentOpenHeaderToFilter.value = null;
}

function resetTable (): void {
    tableUnitsSetting.value = 'inherit';
    orderedHeaders.value = [];
    setHeadersFromProps();
    columnsToHide.value = [];
    updateShownHeaders();
    hideEditTableOverlay();
    saveFilters();
}

function toggleShowTableColumn (columnName: string): void {
    if (columnsToHide.value.includes(columnName)) {
        columnsToHide.value.splice(columnsToHide.value.indexOf(columnName), 1);
    } else {
        columnsToHide.value.push(columnName);
    }
    updateShownHeaders();
    saveFilters();
}

const filterTags = computed<Array<string>>(() => Object.values(currentFiltersByHeading.value).flatMap(filter => {
    if (filter.type === 'text') {
        return Array.from(new Set(filter.options.values.filter(o => o !== null)));
    }

    return [];
}));

async function updateFilters (filter: IHeadingFilters, headerId: string): Promise<void> {
    if (filter.noFilter) {
        if (!currentFiltersByHeading.value[headerId]) return;
        delete currentFiltersByHeading.value[headerId];
    } else {
        currentFiltersByHeading.value[headerId] = filter;
    }

    await filterTableData();
    saveFiltersDebounced();
}

function updateSort (ascending: boolean, property: string): void {
    headerToSort.value.id = property;
    headerToSort.value.ascending = ascending;

    displayedTableData.value = sortTableData(displayedTableData.value);
    triggerRef(displayedTableData);
    emit('update:displayed-rows', displayedTableData.value);
    saveFiltersDebounced();
}

function setInitialTableData (): void {
    // Clear only locally derived filter options so backend-supplied header.uniqueValues survive data refreshes.
    derivedUniqueValuesByHeaderId.value = {};
    initialTableData.value = props.data.data.map((d, i) => {
        const rowId = d.uniqueId ?? `row-${i}`;
        return {
            ...d,
            uniqueId: rowId,
            row: Object.fromEntries(
                Object.entries(d.row).map(([key, vals]) => [
                    key,
                    { ...vals, cellKey: `${rowId}-${key}` },
                ]),
            ),
        };
    });
}

function setHeadersFromProps (): void {
    const headers: Array<IGenericTableHeaderRow> = [];

    Object.values(props.data.headerFirstRowById).forEach(headerVal => {
        if (!headerVal.isHeaderParent) headers.push(headerVal as IGenericTableHeaderRow);
    });

    if (props.data.headerSecondRow?.length) {
        props.data.headerSecondRow.forEach(headerVal => {
            headers.push(headerVal);
        });
    }

    const firstRowKeys = Object.keys(props.data.data[0]?.row ?? {});
    if (!firstRowKeys.length) {
        tableHeaders.value = headers;
        return;
    }

    const firstRowHeaders = firstRowKeys
        .map(headerId => headers.find(header => header.id === headerId))
        .filter((header): header is IGenericTableHeaderRow => !!header);
    const headersMissingFromFirstRow = headers.filter(header => !firstRowKeys.includes(header.id));

    tableHeaders.value = [...firstRowHeaders, ...headersMissingFromFirstRow];
}

function setHeadersFromPropsWithCustomOrder (): void {
    setHeadersFromProps();
    if (orderedHeaders.value.length) sortHeaders(orderedHeaders.value);
}

function sortHeaders (orderedIds: Array<string>) : void {
    orderedHeaders.value = orderedIds;
    if (!orderedHeaders.value.length) return;

    tableHeaders.value.sort((a, b) => orderedHeaders.value.indexOf(a.id) - orderedHeaders.value.indexOf(b.id));
}

function saveFilters (): void {
    if (props.isFixedModule) return;
    const filters: IUserTableFilters = {
        currentFiltersByHeading: currentFiltersByHeading.value,
        headerToSort: headerToSort.value,
        columnsToHide: columnsToHide.value,
        orderedHeaders: orderedHeaders.value,
        unitLocation: tableUnitsSetting.value,
    };
    emit('update:table-filters', filters);
    if (!props.userTableFilterId) return;
    userSettingsActions.updateUserTableFilters({ name: props.userTableFilterId, filters });
}

let saveFiltersTimeoutId: number | null = null;

function saveFiltersDebounced (): void {
    if (saveFiltersTimeoutId !== null) clearTimeout(saveFiltersTimeoutId);
    saveFiltersTimeoutId = window.setTimeout(() => {
        saveFiltersTimeoutId = null;
        saveFilters();
    }, 300);
}

function flushSaveFilters (): void {
    if (saveFiltersTimeoutId === null) return;
    clearTimeout(saveFiltersTimeoutId);
    saveFiltersTimeoutId = null;
    saveFilters();
}

function applyUserTableFilters (userTableFilters: IUserTableFilters | null): void {
    if (!userTableFilters || props.isFixedModule) return;

    if (userTableFilters.currentFiltersByHeading) currentFiltersByHeading.value = userTableFilters.currentFiltersByHeading;
    if (userTableFilters.headerToSort !== undefined) {
        headerToSort.value = userTableFilters.headerToSort ?? { ascending: false, id: null };
    }
    if (userTableFilters.columnsToHide) columnsToHide.value = userTableFilters.columnsToHide;
    if (userTableFilters.orderedHeaders) {
        orderedHeaders.value = [];
        setHeadersFromProps();
        if (userTableFilters.orderedHeaders.length) sortHeaders(userTableFilters.orderedHeaders);
    }
    if (userTableFilters.unitLocation) tableUnitsSetting.value = userTableFilters.unitLocation;
}

function shouldUseCustomHeaderSortSlot (headerId: string): boolean {
    return !!slots['header-sort-control'] && !!props.customSortControlHeaderIds?.includes(headerId);
}

async function initialise (): Promise<void> {
    resetVirtualScroll();
    orderedHeaders.value = [];
    setHeadersFromProps();

    const userTableFilters = userSettingsStore.userSettings.tableFilters[props.userTableFilterId ?? '']?.data ?? null;

    // Backend table filters
    if (props.backendFilters && !userTableFilters) {
        if (props.backendFilters.headerToSort) headerToSort.value = props.backendFilters.headerToSort;
        if (props.backendFilters.currentFiltersByHeading) currentFiltersByHeading.value = props.backendFilters.currentFiltersByHeading;
        if (props.backendFilters.columnsToHide) columnsToHide.value = props.backendFilters.columnsToHide;
        if (props.userTableFilterId) saveFilters();
    }

    // Users saved table filters
    applyUserTableFilters(userTableFilters);

    // hardcoded table filters
    if (props.hardcodedTableFilters) {
        if (props.hardcodedTableFilters.headerToSort) headerToSort.value = props.hardcodedTableFilters.headerToSort;
        if (props.hardcodedTableFilters.currentFiltersByHeading) {
            Object.keys(props.hardcodedTableFilters.currentFiltersByHeading).forEach(key => {
                if (props.hardcodedTableFilters && props.hardcodedTableFilters.currentFiltersByHeading) {
                    currentFiltersByHeading.value[key] = props.hardcodedTableFilters.currentFiltersByHeading[key];
                }
            });
            currentFiltersByHeading.value = props.hardcodedTableFilters.currentFiltersByHeading;
        }
    }

    updateShownHeaders();
    setInitialTableData();
    await filterTableData();
    syncSelectedRowsFromTableData();

    if (props.inverted) {
        nextTick(scrollToBottom);
    }

    emit('update:filters', currentFiltersByHeading.value);
}

watch(() => props.data, async (newData, oldData) => {
    const tabNameChanged = newData.tabName !== oldData.tabName;
    const headerCountChanged = Object.keys(newData.headerFirstRowById).length !== Object.keys(oldData.headerFirstRowById).length;
    const headerOrderChanged = !Object.keys(newData.headerFirstRowById).every((headerKey, index) => Object.keys(oldData.headerFirstRowById)[index] === headerKey);
    const secondRowCountChanged = (newData.headerSecondRow?.length ?? 0) !== (oldData.headerSecondRow?.length ?? 0);
    const secondRowHeaderOrderChanged = (newData.headerSecondRow ?? []).some((header, index) => oldData.headerSecondRow?.[index]?.id !== header.id);

    if (tabNameChanged || headerCountChanged || secondRowCountChanged || headerOrderChanged || secondRowHeaderOrderChanged) {
        await initialise();
        return;
    }

    const scrollTarget = tableWrapperRef.value;
    const keepAtBottom = props.inverted && (!scrollTarget
        || scrollTarget.scrollTop + scrollTarget.clientHeight + 10 >= scrollTarget.scrollHeight);

    setHeadersFromPropsWithCustomOrder();
    if (props.syncSortFromData
        && (newData.headerToSort !== oldData.headerToSort || newData.ascending !== oldData.ascending)) {
        headerToSort.value.id = newData.headerToSort;
        headerToSort.value.ascending = newData.ascending ?? false;
    }
    updateShownHeaders();
    setInitialTableData();
    await filterTableData();
    syncSelectedRowsFromTableData();
    if (keepAtBottom) nextTick(scrollToBottom);
}, { deep: true });

watch(
    () => (props.userTableFilterId ? userSettingsStore.userSettings.tableFilters[props.userTableFilterId]?.data ?? null : null),
    async (userTableFilters) => {
        if (!userTableFilters || props.isFixedModule) return;
        applyUserTableFilters(userTableFilters);
        updateShownHeaders();
        await filterTableData();
        syncSelectedRowsFromTableData();
    },
    { deep: true },
);

watch(() => props.checkable, (isCheckable) => {
    if (!isCheckable) {
        selectedRows.value = [];
        displayedTableData.value.forEach(row => {
            row.selected = false;
        });
        triggerRef(displayedTableData);
        emit('update:selected-rows', []);
        return;
    }

    if (isSelectionControlled.value) {
        applyExternalSelection(props.selectedRowIds ?? []);
    }
});

watch(
    () => (isSelectionControlled.value ? (props.selectedRowIds ?? []).join('\0') : null),
    (selectionKey, previousSelectionKey) => {
        if (!isSelectionControlled.value || !props.checkable) return;
        if (selectionKey === previousSelectionKey) return;
        applyExternalSelection(props.selectedRowIds ?? []);
    },
);

initialise();

onBeforeUnmount(flushSaveFilters);

defineExpose({
    openDownloadTableOverlay,
    showEditTableOverlay,
    externalDownloadExcel,
    formatDataToJson,
});

</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/powerMarkets/genericTable.scss';

</style>
